// Per-biome music mood — picks an ORIGINAL chord progression + tempo + timbres so each
// world has its own groove. Pure + headless-testable: AudioManager reads a MusicMood and
// drives its existing procedural synth with these params. No external files, no copyright.

export type OscType = 'sine' | 'square' | 'sawtooth' | 'triangle'

export interface MusicMood {
  /** chord bank — one triad of base frequencies per progression step */
  progression: number[][]
  /** which chord tone the walking bass steps to, per 8-beat bar */
  bassPat: number[]
  /** notes the sparse lead draws from */
  leadScale: number[]
  /** base seconds-per-step (smaller = faster) */
  step: number
  leadType: OscType
  bassType: OscType
  stabType: OscType
  /** detune cents applied to chord stabs for shimmer/grit */
  shimmer: number
  /** 0..1 — stab octave lift + hi-hat presence (brighter = livelier) */
  brightness: number
}

const WALK = [0, 0, 2, 0, 1, 0, 2, 1]

// Calm, bright lunar plains — i–VI–III–VII in A minor (the original house groove).
const MEADOW: MusicMood = {
  progression: [
    [110.0, 130.81, 164.81], // Am
    [87.31, 110.0, 130.81], // F
    [130.81, 164.81, 196.0], // C
    [98.0, 123.47, 146.83], // G
  ],
  bassPat: WALK,
  leadScale: [220, 246.94, 261.63, 293.66, 329.63, 392, 440],
  step: 0.2,
  leadType: 'square',
  bassType: 'triangle',
  stabType: 'sine',
  shimmer: 40,
  brightness: 0.8,
}

// Warm, swaggering desert — D minor swing, sawtooth bass grit, slightly slower.
const DUNES: MusicMood = {
  progression: [
    [146.83, 174.61, 220.0], // Dm
    [116.54, 146.83, 174.61], // Bb
    [174.61, 220.0, 261.63], // F
    [130.81, 164.81, 196.0], // C
  ],
  bassPat: WALK,
  leadScale: [146.83, 174.61, 196.0, 220, 261.63, 293.66, 349.23],
  step: 0.22,
  leadType: 'sawtooth',
  bassType: 'sawtooth',
  stabType: 'triangle',
  shimmer: 70,
  brightness: 0.6,
}

// Cold, eerie graveyard — E minor, soft triangle voices, slow and sparse.
const GRAVEYARD: MusicMood = {
  progression: [
    [164.81, 196.0, 246.94], // Em
    [130.81, 164.81, 196.0], // C
    [196.0, 246.94, 293.66], // G
    [146.83, 174.61, 220.0], // D
  ],
  bassPat: WALK,
  leadScale: [164.81, 196.0, 220, 246.94, 293.66, 329.63, 392],
  step: 0.24,
  leadType: 'triangle',
  bassType: 'triangle',
  stabType: 'sine',
  shimmer: 30,
  brightness: 0.42,
}

// Tense, driving rift — C minor, sawtooth everything, fast and gritty.
const RIFT: MusicMood = {
  progression: [
    [130.81, 155.56, 196.0], // Cm
    [103.83, 130.81, 155.56], // Ab
    [155.56, 196.0, 233.08], // Eb
    [116.54, 146.83, 174.61], // Bb
  ],
  bassPat: WALK,
  leadScale: [155.56, 174.61, 196.0, 233.08, 261.63, 311.13, 349.23],
  step: 0.18,
  leadType: 'sawtooth',
  bassType: 'sawtooth',
  stabType: 'square',
  shimmer: 95,
  brightness: 0.72,
}

const MOODS: Record<string, MusicMood> = {
  meadow: MEADOW,
  dunes: DUNES,
  graveyard: GRAVEYARD,
  rift: RIFT,
}

/** The per-biome music mood for a map id; the calm meadow groove is the default. */
export function musicMood(mapId: string): MusicMood {
  return MOODS[mapId] ?? MEADOW
}

/**
 * Darken a mood as the run reaches dusk (night = 0 early .. 1 deep): the groove gets a touch
 * slower and heavier, dimmer (stabs sink toward the base octave, hats thin out), and eerier
 * (more stab detune). Pure — same (mood, night) always yields the same shifted mood.
 */
export function nightShift(mood: MusicMood, night: number): MusicMood {
  const n = Math.max(0, Math.min(1, night))
  if (n === 0) return mood
  return {
    ...mood,
    step: mood.step * (1 + n * 0.12), // slightly slower / heavier at night
    brightness: mood.brightness * (1 - n * 0.5), // darker
    shimmer: mood.shimmer + n * 40, // eerier detune
  }
}
